/* ============================================================================
 * DCS Bridge
 *
 * The translation layer between the listener and the engine. The listener
 * emits human colour keys per region (what the customer picked). The engine
 * reveals photographed swatches through masks, so it wants swatch image URLs,
 * not colour names. The bridge is the "resolve selections into colour values"
 * step of the listener's job in the reveal stack (STRATEGY.md):
 *
 *     colour key ("CV-Garnet")  ->  swatch URL pair {f,b}  ->  engine.render
 *
 * PROVEN in the harness 2026-06-14 on alicia (duo):
 *   DCSBridge.render({ regions: { top: "CV-Garnet", bottom: "N-Arctic" },
 *                      trimColor: "N-Rose" })
 *   -> top, bottom, trim all revealed. A missing swatch (t-buttercup) fails
 *      soft: the region is left unpainted and a warning is logged, never a crash.
 *
 * Engine contract (ARCHITECTURE.md sec.10):
 *   DCS.registerStyle(styleId, manifest)
 *   DCS.mount({ frontCanvas, backCanvas, styleId })
 *   DCS.render({ styleId, top, bottom, trim, ... })   // each colour an {f,b} pair
 *   The engine is window.DCS in the Liquid sidecar, an ES module in the harness.
 *
 * This file is engine-source-agnostic: it uses window.DCS by default, and the
 * harness can hand it the module form via DCSBridge.config.engine.
 * ========================================================================== */

(function () {
  'use strict';

  // Debug-aware logger. Uses sidecar-installed window.__DCS_LOG__ when
  // present; falls back to the same URL-param rule if not set (bridge.js
  // is only loaded by the sidecar today, but the fallback keeps a
  // standalone-load scenario safe). console.error is always unconditional.
  var LOG = (function () {
    if (typeof window === 'undefined') {
      return { log:function(){}, info:function(){}, warn:function(){}, error:function(){ if(typeof console!=='undefined') console.error.apply(console,arguments); }, table:function(){} };
    }
    if (window.__DCS_LOG__) return window.__DCS_LOG__;
    var q = new URLSearchParams(window.location.search);
    var d = q.has('dcs_debug') || q.has('dcs_gen') || q.has('dcs_swatch_test');
    window.__DCS_LOG__ = {
      log:   d ? console.log.bind(console)  : function(){},
      info:  d ? console.info.bind(console) : function(){},
      warn:  d ? console.warn.bind(console) : function(){},
      error: console.error.bind(console),
      table: d && console.table ? console.table.bind(console) : function(){}
    };
    return window.__DCS_LOG__;
  })();

  // Plain-single factory for _SEED_COLOURWAYS entries. Returns the default
  // single shape; per-style deviations are passed as an explicit override
  // object so they read at a glance (e.g. mkSingle({ trimDecoupled: {...} })).
  // The bridge stays the explicit inventory — every style still gets its own
  // entry — this only collapses the boilerplate. A step toward the queued
  // harness-derives-from-bridge refactor, not a wildcard. Object.assign yields
  // a fresh object per call (no shared-reference aliasing) and is byte-
  // equivalent to the prior inline literals, so preflight stays green.
  function mkSingle(extra) {
    return Object.assign({ archetype: 'single', sleeve: null, removeTrim: false }, extra || {});
  }

  var Bridge = {

    /* ----- the two things that differ between harness and production ----- */
    config: {
      // Which style this page shows. Callers MUST set this explicitly
      // (sidecar from Liquid product → style; harness from its setup).
      // No default — mount() throws if unset, so a misconfigured page
      // fails commerce-safely rather than silently rendering a wrong
      // style's masks. The token is the only resolution boundary.
      styleId: null,

      // Where swatches and masks are served from. Local harness paths by
      // default; in production point these at the Shopify Files base.
      swatchBase: '/assets/swatches',
      maskBase:   '/assets/styles',

      // Asset layout. Local repo nests masks under maskBase/<style>/...;
      // Shopify Files is flat (no folders), so masks sit directly under
      // maskBase as <style>-<region>-mask-<side>.png. Set true for Shopify.
      // Swatches are already flat in both layouts, so this only affects masks.
      flatAssets: false,

      // Preferred swatch extension, with fallback. CV/V are mid-migration
      // PNG -> JPG, so we try jpg then png. Once migration completes: ['jpg'].
      swatchExts: ['jpg', 'png'],

      // VALIDATION ONLY — temporary swatch test-name override. Maps a canonical
      // swatch slug to a test-name suffix so resolveSwatch fetches `<slug><suffix>`
      // instead of `<slug>`. Used to validate resized swatches under throwaway
      // `__t<n>` names without overwriting the deletion-blocked production file.
      // MUST be empty ({}) in any committed/production build. Set transiently
      // during real-store validation, removed at Stage F. Never wire a test
      // swatch into a product metafield — that would reference-lock it and
      // recreate the deletion block this exists to avoid.
      //
      // Usage at runtime (browser console on the duplicated validation theme):
      //   DCSBridge.config.swatchTestOverride = { 'cv-garnet': '__t1', 'cv-amaretto': '__t1' };
      // Iterate: bump '__t1' to '__t2'. Exit: set back to {}.
      //
      // URL shape during validation: <swatchBase>/cv-garnet__t1-f.jpg?v=<N>.
      // Name uploaded test files exactly: <key>__t<n>-<side>.<ext>.
      swatchTestOverride: {},

      // VALIDATION BENCH — generational mask suffix. Inserts the suffix
      // immediately before the trailing `-<side>.png` of every constructed
      // mask URL (style masks, sleeve masks, sleeve-trim masks, hollows).
      // Empty (default) → no suffix → URLs byte-identical to canonical.
      // Set via the sidecar from `?dcs_gen=tN` (yielding `__tN`); pre-mount
      // so the seed-paint manifest carries the suffix from frame one.
      // Companion to swatchTestSuffix; both flip together on `?dcs_gen=tN`.
      maskTestSuffix: '',

      // VALIDATION BENCH — generational swatch suffix. Global counterpart
      // to swatchTestOverride (per-key); applies the same `__tN` to ALL
      // swatches resolved by resolveSwatch unless a per-key override is
      // already set for that slug. Empty (default) → no suffix. Set via
      // the sidecar from `?dcs_gen=tN`. Precedence in resolveSwatch:
      //   swatchTestOverride[slug] > swatchTestSuffix > '' (canonical).
      swatchTestSuffix: '',

      // Engine reference. Defaults to window.DCS (the sidecar global). The
      // harness, which loads the engine as a module, sets this to the module's
      // default export instead.
      engine: null
    },

    // Asset version token (2026-06-25). Appended as `?v=` to every Shopify
    // Files asset URL constructed by the bridge — style masks, sleeve masks,
    // sleeve-trim masks, the alicia trim override, and swatches (both the
    // HEAD existence probe and the returned URL pair). Shopify's image CDN
    // caches keyed on URL path PLUS ?v=<n>; bumping this constant forces a
    // cache miss for every constructed URL on the next page load, returning
    // freshly re-uploaded bytes for assets that kept their canonical name.
    // Arbitrary query params (e.g. ?nocache=1) do NOT defeat the cache —
    // only ?v=<n> with a NEW value Shopify hasn't seen before.
    //
    // BUMP PROCEDURE: whenever any Shopify Files asset is re-uploaded under
    // its canonical name (mask, swatch, sleeve), bump this integer by one,
    // commit, re-deploy bridge.js to the theme. Subsequent customer page
    // loads fetch the fresh bytes. The mask-rename / mask-delete dance is
    // no longer needed for cache invalidation.
    //
    // COMMITTED VALUE: '3'. The committed file ships at the baseline value;
    // deploy-time bumps (e.g. '3' → '4' to invalidate edge cache for a
    // re-uploaded asset) are install decisions, not code-commit decisions —
    // bump locally at install, deploy, then leave the committed file at the
    // baseline so subsequent edits don't carry stale deploy-time values.
    // History: '1' and '2' were manually probed during cache-busting
    // investigation 2026-06-25 and may have cached entries with stale bytes;
    // '3' is the first untouched baseline value.
    _ASSET_VERSION: '4',

    // ALIAS LAYER (interim inline; Costin ruling 2026-07-15). APO-served dropdown
    // spelling -> swatch canon: the dropdown spelling is customer-facing fact, the
    // swatch corpus is canon, this bridges the two with NO renames. Applied in
    // resolveSwatch AFTER normalization, BEFORE resolution. INTERIM: this table is
    // inlined here for a data-only, sidecar-untouched paste; it MUST stay in sync
    // with data/color-aliases.json (the audit source of truth) until externalized —
    // the sidecar will fetch that file into config.aliases at the next deliberate
    // sidecar deploy, and this constant retires then. m-pinnkberry resolves only
    // once its swatch is delivered (fail-soft miss until Raul).
    _ALIASES: {
      'n-starling':   'n-sterling',
      't-blueball':   't-bluebell',
      't-pistachoi':  't-pistachio',
      'm-pinnkberry': 'm-pinkberry'
    },

    // Region name → engine mount key. Single global map; mirrors the harness's
    // REGION_MOUNT_KEY (test/harness.html ~L205-218). Engine's manifest consumer
    // (engine.js registerStyle / mount / render) expects these exact strings.
    _REGION_MOUNT_KEY: {
      'body':          'body',
      'body-top':      'bodyTop',
      'body-middle':   'bodyMiddle',
      'body-bottom':   'bodyBottom',
      'trim':          'trim',
      'zipper':        'zipper',
      // dual-top-and-bottom (kayoko only — see _REGION_TO_ARG below for the
      // load-bearing rationale). Recipe regions map to existing engine slots
      // rather than introducing new ones: top-color1 paints into bodyTop
      // (the first iteration of the engine's multi-body paint loop), top-
      // color2 paints into bodyMiddle (second iteration), bottom-color
      // paints into bodyBottom (third iteration). This is a deliberate slot-
      // repurpose, not a horizontal-band semantic — see _REGION_TO_ARG.
      'top-color1':    'bodyTop',
      'top-color2':    'bodyMiddle',
      'bottom-color':  'bodyBottom',
      'hollow':        'hollow',
      'hollow-top':    'hollowTop',
      'hollow-middle': 'hollowMiddle',
      'hollow-bottom': 'hollowBottom'
    },

    // Archetype → { region list, manifest type }. Mirrors the harness's
    // ARCHETYPES table (test/harness.html ~L86-136) plus its type-field rule
    // (L255: `archetype.startsWith('single') ? 'single' : 'duo'`). Seeded with
    // the two archetypes wired today; mimi (single-with-body-top), kayoko
    // (dual-top-and-bottom), trio (micah/lucy), and charlotte
    // (single-with-front-zipper) add cheaply once their seeds land.
    _ARCHETYPE_RECIPES: {
      single: {
        type: 'single',
        regions: ['body', 'trim', 'hollow']
      },
      duo: {
        type: 'duo',
        regions: ['body-top', 'body-bottom', 'trim', 'hollow-top', 'hollow-bottom']
      },
      // trio (micah, lucy). Mirrors harness ARCHETYPES.trio byte-for-byte.
      // type: 'duo' per harness L255 rule (non-single archetypes report
      // 'duo' so engine.js sideUrls's isMultiBody resolves true and the
      // bodyTop/bodyMiddle/bodyBottom paint branch runs). bodyMiddle paint
      // already exists at engine.js:139; harness-proven on micah/lucy.
      trio: {
        type: 'duo',
        regions: ['body-top', 'body-middle', 'body-bottom', 'trim', 'hollow-top', 'hollow-bottom']
      },
      // single-with-front-zipper (charlotte). Single body + structural trim
      // + front-only zipper accent + engine-derived hollow. Zipper is
      // declared in `regions` so buildMountManifest emits its mask URL,
      // but is deliberately absent from _REGION_TO_ARG — that filtering
      // keeps it out of the master seed cycle, honoring MANIFEST's
      // `zipper: fall_through: body`. Engine.js sideUrls's existing
      // `pick(zipper, s) || pick(body, s)` fall-through paints the zipper
      // mask with the body swatch from frame one, no transition flash.
      // `frontOnly: ['zipper']` tells buildMountManifest to skip the b-side
      // URL (live confirms front-only; MANIFEST sides: [f]). Live PDP
      // confirms no "Match Body Color" sentinel chip — match-body is
      // achievable structurally (Zipper-not-Yes hides the control,
      // listener filters out the hidden group, args.zipper unset, engine
      // fall-through paints).
      'single-with-front-zipper': {
        type: 'single',
        regions: ['body', 'trim', 'zipper', 'hollow'],
        frontOnly: ['zipper']
      },
      // dual-top-and-bottom (kayoko, the only inheriting style). Mirrors
      // harness ARCHETYPES.dual-top-and-bottom byte-for-byte. type: 'duo' so
      // engine.js's `isMultiBody = !mounted.masks.front.body` resolves true
      // (kayoko has no `body` mask key — its body regions mount under
      // bodyTop/bodyMiddle/bodyBottom via the _REGION_MOUNT_KEY slot-repurpose
      // documented above). The multi-body paint branch iterates those three
      // slots correctly. Hollow is unzoned per MANIFEST (kayoko-hollow-{f,b}
      // on disk) with fall_through: bottom-color — the engine's hollow
      // dispatch chain (engine.js, 2026-06-23 fix) routes unzoned 'hollow' to
      // bodyBottom when urls.bodyBottom is set, satisfying the MANIFEST
      // contract without an additional dispatch table.
      'dual-top-and-bottom': {
        type: 'duo',
        // Hollow shape is MIXED: front uses unzoned `hollow` (kayoko-hollow-f.png,
        // falls through to bottom-color per the engine.js Diff 1 chain), back
        // uses zoned `hollow-top` (tracks top-color1 via hollowTop → bodyTop)
        // and `hollow-middle` (tracks top-color2 via hollowMiddle → bodyMiddle
        // — kayoko is the first style to populate hollowMiddle in production;
        // engine dispatch was already live). The back aperture reveals the
        // inside of the front; each half tracks the front top it covers, so
        // the two back-hollow halves color-match the two front tops.
        regions: ['top-color1', 'top-color2', 'bottom-color', 'trim', 'hollow', 'hollow-top', 'hollow-middle'],
        // Per-region sides constraints (2026-06-24 console cleanup): the
        // mixed-shape hollow has front-only `hollow` and back-only zoned
        // halves. Without these, buildMountManifest emits f+b for every
        // region and the three phantom URLs (kayoko-hollow-b,
        // kayoko-hollow-top-f, kayoko-hollow-middle-f) 404 on the wire even
        // though loadMaskSet fail-soft absorbs them. Honest declaration here
        // eliminates the wire-level noise before the live store cold-load.
        frontOnly: ['hollow'],
        backOnly:  ['hollow-top', 'hollow-middle']
      }
    },

    // Seed neutral (was two-token cycle T-Lapis / T-Pool, collapsed
    // to a single neutral 2026-07-03). Single-element array keeps
    // seedFor's cycle-index logic untouched (cycle[i % 1] === cycle[0]
    // for every region) — data-only change, not a mechanism change.
    // Every region seeds N-Antique, a neutral cream nylon; render()'s
    // null-skip preserves it for regions the customer hasn't touched,
    // giving a blank-canvas behaviour on partial configs. Per-region
    // override still lives on the optional `seed` field of individual
    // _SEED_COLOURWAYS entries — unpopulated today, reserved for the
    // velvet/mesh exception when a specific seed lands off-key.
    _SEED_CYCLE: ['N-Antique'],

    // Recipe region name → engine.render arg name. The recipe carries
    // long-form zone names (body-top, body-bottom) for clarity; the
    // engine signature (engine.js render() params) takes the short
    // forms (body, top, middle, bottom). Hollows are absent from this
    // map on purpose — engine-derived (luma-shift on the adjacent body
    // region), so they are filtered out by orderedRegions() before
    // seed cycling.
    _REGION_TO_ARG: {
      'body':          'body',
      'body-top':      'top',
      'body-middle':   'middle',
      'body-bottom':   'bottom',
      'trim':          'trim',
      // ─────────────────────────────────────────────────────────────────────
      //  Kayoko slot-repurpose (dual-top-and-bottom, the ONLY style in this
      //  archetype per MANIFEST and the only style that will ever produce
      //  "Top Color 1" / "Top Color 2" / "Bottom Color" APO labels).
      //
      //  Why does top-color2 map to the engine's `middle` slot?
      //
      //  The engine.js render() signature has body, top, middle, bottom — a
      //  vertically-stacked-band mental model inherited from the duo and trio
      //  archetypes. Kayoko's body is NOT a horizontal stack: it has two
      //  side-by-side vertically-split top panels plus a bottom. There's no
      //  natural `middle` band on kayoko.
      //
      //  We could have extended engine.js with new params topColor1, topColor2,
      //  bottomColor and a parallel set of paint-loop entries (Option 1 in the
      //  2026-06-23 audit). We chose the slot-repurpose instead because:
      //
      //  (1) Engine stays style-agnostic. It paints whatever masks are mounted
      //      under bodyTop/bodyMiddle/bodyBottom with the swatches arriving as
      //      args.top/middle/bottom. The semantic meaning of "middle" is
      //      injected by the bridge layer here, not by the engine.
      //  (2) Listener regression risk on "Bottom Color" is zero. Duo's
      //      "Nylon Bottom Color" already returns 'bottom' from regionToken;
      //      kayoko's "Nylon Bottom Color" returns the same token. No need to
      //      branch the listener on archetype context (which it doesn't have).
      //  (3) Engine paint-loop iterates bodyTop → bodyMiddle → bodyBottom in
      //      a fixed order. Under this mapping, top-color1 paints first,
      //      top-color2 paints second, bottom-color paints third. On
      //      authored-non-overlapping masks this is invisible; on accidental
      //      overlap the second-painted (top-color2) wins.
      //
      //  This mapping is sealed for kayoko ONLY. No other style produces
      //  top-color1/top-color2/bottom-color region tokens. The slot-repurpose
      //  is not a general pattern; if a second dual-top-and-bottom style ever
      //  ships, this comment is the place to verify the contract still holds.
      //
      //  If a future maintainer wonders why top-color2 → middle: it's the
      //  bodyMiddle paint slot, not a horizontal band. Do not "fix" this to
      //  → topColor2 unless engine.js has been extended with that param and
      //  the parallel paint-loop entry has landed.
      // ─────────────────────────────────────────────────────────────────────
      'top-color1':    'top',
      'top-color2':    'middle',
      'bottom-color':  'bottom'
    },

    // Canonical body-zone paint order, bottom → top. orderedRegions()
    // sorts body regions by this rank before _SEED_CYCLE walks them,
    // so the seed lands deterministically regardless of how the
    // archetype recipe happens to list its zones.
    _BODY_PAINT_ORDER: ['bottom', 'middle', 'top'],

    // Per-style declaration table. The seed colourway itself is DERIVED
    // at mount-time by seedFor() — region order alternated through
    // _SEED_CYCLE. Each entry carries: archetype (required; drives both
    // buildMountManifest and seedFor), sleeve/removeTrim defaults (read
    // directly by mount() below — fields preserved here intentionally
    // so those reads stay byte-identical mid-deploy), and an optional
    // `seed` override block for the rare style whose derived colourway
    // lands off-key. Override keys are short-form engine arg names
    // (top, bottom, trim, body, middle); values replace the derived
    // position before the trim/regions split.
    //
    // Adding a new style: one line, archetype only — derivation
    // handles the rest. The Shopify-side prerequisites (masks on Files,
    // dcs.style_token metafield populated to match the key here) are
    // verified at cold-load, not in this repo.
    //
    // F1 fail-fast at mount() gates on entry existence, so an unwired
    // style is rejected before any mask load. Colour derivation: since
    // 2026-07-03 the seed constant is the single neutral N-Antique;
    // every region on every wired style seeds to N-Antique at mount.
    // Historically: cv-garnet / t-wisteria were alicia's transitional
    // per-style seed; the T-Lapis / T-Pool two-token cycle replaced it
    // 2026-06-19; that cycle collapsed to a single neutral 2026-07-03.
    // The listener's picker-driven render lands on the customer's
    // choice immediately after mount, so the steady-state canvas is
    // whatever the customer picked — the seed is just the pre-picker
    // starting state.
    _SEED_COLOURWAYS: {
      // Tier 1 batch-port from MANIFEST.md (2026-06-19): every single + duo
      // style in the manifest's per-style entries. Master cycle + harness-
      // proven single/duo production paths mean each fires live once Costin
      // sets its dcs.style_token metafield and uploads the {style}-* masks
      // to Shopify Files. Until then the F1 fail-fast keeps the static up.
      // Tier 2 archetypes (single-with-front-zipper / trio / single-with-
      // body-top / dual-top-and-bottom) are held until their plumbing
      // audits land — see HANDOFF entry for the per-archetype gaps.
      // regionsAbsent (2026-07-07): per-side declaration of hollow zones
      // absent by design. Preflight (tools/preflight.js) consumes this today
      // to classify the disk-vs-recipe diff into BLOCKING vs EXPECTED-ABSENT
      // buckets.
      //
      // Hollow presence is a PER-STYLE FACT, not a sub-class. The archetype
      // recipe is maximal (derives every candidate hollow zone); whether a
      // hollow fires is decided by the file existing on disk (loadMaskSet
      // soft-fails absence). regionsAbsent here declares which absences are
      // BY DESIGN, so preflight can separate an intended absence
      // (EXPECTED-ABSENT) from a real gap (BLOCKING) or a stale declaration
      // (DECLARATION STALE). Authoring rationale (full text: ARCHITECTURE.md
      // "Hollows are a per-style fact"): a hollow exists only where the
      // garment has line-of-sight to the far side, so a closed front carries
      // no front hollow. That explains the asset; it does NOT dictate a file
      // set. Certification is disk + the harness gateway (every zone eye-
      // cycled, both views, before upload), never category reasoning. Adjust
      // individual entries when reality deviates; do not sub-class.
      // (Cautionary: the 2026-07-02 micah "mirror of lucy" record came from
      // category reasoning and was wrong; disk carried the true mapping.)
      //
      // Bridge runtime does NOT consume this yet: it still emits every
      // archetype-recipe URL and relies on loadMaskSet's fail-soft for
      // hollow / trim keys. Intended SECOND CONSUMER is the mesh /
      // hollowShape bridge session, where bridge runtime will honour
      // the declaration and skip emission of declared-absent URLs.
      // One declaration, two consumers, no drift.
      //
      // Provenance: alicia + lucy were bootstrapped from test/harness.html
      // STYLES[].regions_absent on 2026-07-07, then confirmed against on-disk
      // reality by the preflight run the same day (DISK AGREES for both), so
      // their markers now read disk-confirmed. micah was disk-confirmed from
      // the start (resolved the inverted 2026-07-02 "mirror" record). Any
      // future entry bootstrapped from the harness should carry a
      // `// harness-derived` marker until a preflight run confirms it.
      // alex: single, genuinely sleeveless, plain trim. Racerback — CERTIFIED
      // 2026-07-14 (front neckline aperture + back lower-scoop + both shoulder
      // apertures all deriving from body; trim traces the full racer geometry).
      // Named for Alexandra (Costin's wife, ex-ballerina) at original release.
      alex:      { archetype: 'single', sleeve: null, removeTrim: false },
      alicia:    { archetype: 'duo',    sleeve: null, removeTrim: false, trimDecoupled: { main: ['f', 'b'], noSleeve: ['f', 'b'] }, regionsAbsent: { 'hollow-top': ['f'] } },   // disk-confirmed 2026-07-07 (preflight)
      amanda:    { archetype: 'duo',    sleeve: null, removeTrim: false, regionsAbsent: { 'hollow-top': ['f'] } },   // CERTIFIED 2026-07-14 (was harness-derived gateway 2026-07-08)
      anna:      { archetype: 'single', sleeve: null, removeTrim: false },   // AWAITING ASSETS (ruling: single + trim + sleeves; zero masks on disk 2026-07-14)
      'anna-duo': { archetype: 'duo', sleeve: null, removeTrim: false },   // AWAITING ASSETS (ruling: duo + trim + sleeves; zero masks; hollow topology TBD on delivery) (2026-07-14)
      charlotte: { archetype: 'single-with-front-zipper', sleeve: null, removeTrim: false, trimDecoupled: { main: ['b'], noSleeve: ['f', 'b'] } },
      daniela:   { archetype: 'single', sleeve: null, removeTrim: false },
      // daniela-duo: intended SEPARATE style per the identity rule (different
      // seams = separate style; precedent laura-duo), not a cleanup target.
      // Duo archetype, no sleeve. Front-TOP hollow absent by design (closed upper
      // front, alicia's front shape) -> regionsAbsent hollow-top ['f']. Front-BOTTOM
      // hollow (leg openings) DELIVERED 2026-07-14: daniela-duo-hollow-bottom-f
      // landed, correctly named per the hollow family's NO-INFIX convention
      // (<style>-hollow-<zone>-<side>.png; maskUrl builds hollow regions without
      // the -mask- infix). 'hollow-bottom': ['f'] DROPPED from regionsAbsent so it
      // now paints (the stale declaration was blocking a file that exists).
      //
      // BACK-BODY RESOLVED 2026-07-14 (catalog garment ruling): her back is OPEN;
      // the top's back presence is a lateral strip visible only in side view, so
      // from the back only TRIM shows. Handled WITHOUT an architecture change via
      // a SENTINEL EMPTY MASK, not a declaration:
      //   - daniela-duo-body-top-mask-b = a fully transparent 1000x1500 PNG
      //     (0-alpha everywhere) -> satisfies the duo loader contract, paints
      //     nothing on the wire. CONVENTION: an empty mask = declared
      //     non-presence at that layer.
      //   - the former unsplit daniela-duo-body-mask-b was bounds-verified as the
      //     BELOW-SEAM bottom region (frac 0.47..0.925 == front bottom) and
      //     renamed to daniela-duo-body-bottom-mask-b (denise precedent).
      // Back hollows (hollow-top-b/hollow-bottom-b) derive colour from the body
      // TOKEN via deriveHollowColor (swatch-sampled, not mask-pixel), so the
      // empty body-top mask does not wobble them.
      'daniela-duo': { archetype: 'duo', sleeve: null, removeTrim: false, regionsAbsent: { 'hollow-top': ['f'] } },
      // daria: duo, current-scheme fetches. Legacy daria-hollow-back-{top,bottom}-b
      // orphans on disk stay flagged for cleanup (old hollow-back-* naming); this
      // entry derives from the current zoned scheme only.
      daria:     { archetype: 'duo', sleeve: null, removeTrim: false, regionsAbsent: { 'hollow-top': ['f'] } },   // harness-derived (gateway 2026-07-08, pending confirmation)
      // denise: duo, plain trim (no decouple; disk has denise-trim-mask-f/b).
      // Front-hollow zone token RESOLVED 2026-07-14: denise-hollow-f renamed to
      // denise-hollow-bottom-f, so the front aperture now paints via the zoned duo
      // recipe. Same hollow shape as alicia now — front-top absent, front-bottom +
      // both back present -> regionsAbsent hollow-top: ['f']. (2026-07-14)
      denise:    { archetype: 'duo', sleeve: null, removeTrim: false, regionsAbsent: { 'hollow-top': ['f'] } },
      // elise: single, plain trim, BOTH hollow sides present (unusual for a
      // single). Includes a straight-through aperture whose fall-through chain
      // terminates at the photographic base. Standard unzoned-hollow resolve, no
      // special casing. Cert eyeball: through-hole reads as untouched base (no
      // luma tint), front/back agree on the absence. (2026-07-14)
      elise:     { archetype: 'single', sleeve: null, removeTrim: false },
      // erica: front-revealing duo. All four hollow zones present on disk
      // (top f/b, bottom f/b) after Costin's 2026-07-08 bttom->bottom typo
      // rename, so NO regionsAbsent. Structurally the opposite of amanda/daria
      // (closed-front): same duo box, opposite front line-of-sight, disk
      // decides. Legacy erica-hollow-back-* files (if any remain) are cleanup.
      erica:     { archetype: 'duo', sleeve: null, removeTrim: false },   // harness-derived (gateway 2026-07-08, pending confirmation)
      heather:   { archetype: 'single', sleeve: null, removeTrim: false },
      jaione:    mkSingle(),   // CERTIFIED 2026-07-14 (was harness-derived gateway 2026-07-09)
      // jessica: charlotte clone with NO back opening (entry via front zipper).
      // Same archetype/recipe as charlotte, but trimDecoupled.main is [] on BOTH
      // sides by construction: the zipper occupies the closure line, so there is
      // no main trim binding anywhere (no jessica-trim-main-mask ever owed). Only
      // the no-sleeve trim segment exists per side. hollow-f exists (leg-opening
      // interior, unzoned, falls through to body); hollow-b absent by design ->
      // regionsAbsent (per-style fact, not a sub-class). (2026-07-14)
      jessica:   { archetype: 'single-with-front-zipper', sleeve: null, removeTrim: false, trimDecoupled: { main: [], noSleeve: ['f', 'b'] }, regionsAbsent: { 'hollow': ['b'] } },
      julia:     { archetype: 'single', sleeve: null, removeTrim: false },   // harness-derived (gateway 2026-07-09, pending confirmation); single, both hollow sides present, no regionsAbsent
      kate:      { archetype: 'single', sleeve: null, removeTrim: false },   // harness-derived (gateway 2026-07-09, pending confirmation)
      // kayoko: side-split tokens bind to WEARER ANATOMY, constant across views
      // (garment testimony from Costin, 2026-07-08). Top Color 1 = wearer's
      // RIGHT panel in every view. CERTIFIED FIX (2026-07-09, render vs the
      // two-tone catalog photo): the delivered front top pair was assigned to
      // the wrong wearer panels; swapping kayoko-top-color1-mask-f <->
      // kayoko-top-color2-mask-f makes the render match the garment. Back pair
      // + hollows certified correct as-is (untouched by the final fix).
      // Reaching this took three tries; the dead ends are recorded in HANDOFF
      // (2026-07-09) so they are not retried. Cross-view precondition: an
      // f-mask and a b-mask can only trade jobs because kayoko's front and back
      // canvases share pose/footprint closely enough that a panel mask
      // registers on either view — certified by the render's clean silhouette
      // edges on both views (ragged edges would have meant f/b footprints
      // differ, a Frankie re-cut). Certifier for side-split styles is the
      // two-tone catalog photo, NOT internal front/back consistency. See
      // ARCHITECTURE.md "Side-split token binding".
      //
      // SLOT-REPURPOSE MAP (kayoko runs on the trio/micah machinery BY DESIGN;
      // her dual-top-and-bottom garment zones ride the engine's top/middle/
      // bottom slots — there is no vertical "middle" band on kayoko). The
      // hollow filenames are SLOT-named; do NOT rename them to garment
      // vocabulary — the render path keys on the slot, not the panel.
      //   garment (wearer)    APO label       token / mask file           slot(mountKey)  arg
      //   right top panel     "Top Color 1"   top-color1 (…-mask)         bodyTop         top
      //   left top panel      "Top Color 2"   top-color2 (…-mask)         bodyMiddle      middle
      //   bottom              "Bottom Color"  bottom-color (…-mask)       bodyBottom      bottom
      //   front inner reveal  (none)          hollow (kayoko-hollow-f)    hollow          falls through to bottom-color
      //   back reveal, R top  (none)          hollow-top (…-hollow-top-b) hollowTop       tracks bodyTop = top-color1
      //   back reveal, L top  (none)          hollow-middle(…-middle-b)   hollowMiddle    tracks bodyMiddle = top-color2
      // Binding certified 2026-07-09: Top Color 1 = wearer's RIGHT panel, both views.
      // frontOnly ['hollow'] is a GARMENT fact (front = one unzoned reveal; back
      // = two zoned reveals), enforced by the recipe's frontOnly/backOnly — not
      // a slot fact. The garment<->slot vocabulary translation point is
      // listener.js regionToken ("Top Color 1"/"Top Color 2" -> top/middle);
      // the customer-facing Mageworx labels speak garment vocabulary, files +
      // engine speak slot vocabulary. Engine is style-agnostic. OPEN (Raul
      // garment-confirm): the physical geometry of the two back hollows (side-
      // by-side vs stacked) — the COLOR-tracking above is certain, the shape is
      // Raul's to confirm from the garment. Do NOT "fix" the slot names.
      kayoko:    { archetype: 'dual-top-and-bottom', sleeve: null, removeTrim: false },
      // kids-anna: single + plain trim. CERTIFIED SLEEVELESS 2026-07-14 (her live
      // config: back scoop deriving, trim tracing full geometry, leg openings
      // correct). Sleeve exercise (harness) mounts + paints BUT the shoulder
      // junction fails on her sleeveless-authored body (caps misaligned, doubled
      // back-shoulder edges, front armhole pinch) -> FIRST visual specimen for the
      // mesh junction spike. Sleeve enablement needs junction-compatible body masks,
      // NOT just an APO toggle. (2026-07-14)
      'kids-anna': { archetype: 'single', sleeve: null, removeTrim: false },
      // kids-anna-duo: duo + plain trim + sleeves (APO-gated). Front hollow was
      // authored unzoned (kids-anna-duo-hollow-f); bounds-verified below-seam
      // (vfrac 0.76..0.92 = leg opening) and renamed to hollow-bottom-f (denise
      // precedent). Back body fully split (no sentinel). -> alicia hollow shape,
      // regionsAbsent hollow-top ['f']. (2026-07-14)
      'kids-anna-duo': { archetype: 'duo', sleeve: null, removeTrim: false, regionsAbsent: { 'hollow-top': ['f'] } },   // CERTIFIED 2026-07-14 (leg openings; kids tray = effective noSleeve live)
      'kids-daniela': { archetype: 'single', sleeve: null, removeTrim: false },   // clone of adult daniela (single, both hollow sides, plain trim) (2026-07-14)
      // kids-daniela-duo: kids clone of daniela-duo, identical open-back topology
      // (catalog ruling, adult precedent). Back-top body = transparent SENTINEL
      // mask (kids-daniela-duo-body-top-mask-b, 1000x1500 0-alpha — empty mask =
      // declared non-presence). Former unsplit body-mask-b bounds-verified as
      // below-seam bottom (frac 0.47..0.925) and renamed to body-bottom-mask-b.
      // Front-TOP hollow absent by design. Front-BOTTOM hollow (leg openings)
      // RESOLVED 2026-07-14 by INTERNAL COPY: kids-daniela-hollow-f copied to
      // kids-daniela-duo-hollow-bottom-f (single->duo, validated on the adults where
      // daniela-hollow-f == daniela-duo-hollow-bottom-f byte-for-byte: same
      // leg-opening geometry, same garment base + kids scale). 'hollow-bottom': ['f']
      // dropped. Back hollows derive from the body token (deriveHollowColor),
      // unaffected by the empty top mask. (2026-07-14)
      'kids-daniela-duo': { archetype: 'duo', sleeve: null, removeTrim: false, regionsAbsent: { 'hollow-top': ['f'] } },
      // kids-daria: duo, daria's exact shape. The former kids-daria-hollow-f
      // was content-confirmed 2026-07-08 as a single-zone BOTTOM aperture
      // (zone token omitted) and renamed to kids-daria-hollow-bottom-f, so she
      // is hollow-bottom f/b + hollow-top-b = closed front-top. Legacy
      // kids-daria-hollow-back-* orphans stay flagged for cleanup.
      'kids-daria': { archetype: 'duo', sleeve: null, removeTrim: false, regionsAbsent: { 'hollow-top': ['f'] } },   // harness-derived (gateway 2026-07-08, pending confirmation)
      'kids-julia': { archetype: 'single', sleeve: null, removeTrim: false },   // straight clone of adult julia (2026-07-14)
      'kids-marieke': { archetype: 'single', sleeve: null, removeTrim: false },   // kids single (adult marieke has zero masks; own token, no dependency) (2026-07-14)
      'kids-marisa': { archetype: 'single', sleeve: null, removeTrim: false },   // kids clone of marisa (single) (2026-07-14)
      'kids-sarah': { archetype: 'single', sleeve: null, removeTrim: false },   // kids clone of sarah (single) (2026-07-14)
      // kids-sofiane: single + PLAIN trim (adult sofiane's decoupled trim does
      // NOT apply — kids disk has one trim-mask per side, no main/no-sleeve split).
      // Sleeves ride the generic per-view set (APO-gated live by design; harness
      // exercises them for cert). Both hollow sides present; no adult-sofiane
      // hollow-naming collision (clean hollow-f/b). (2026-07-14)
      'kids-sofiane': { archetype: 'single', sleeve: null, removeTrim: false },
      kiki:      { archetype: 'single', sleeve: null, removeTrim: false },
      larissa:   { archetype: 'single', sleeve: null, removeTrim: false },
      // laura-duo: Costin renamed the former unzoned laura-duo-hollow-b to
      // hollow-top-b on 2026-07-08, so disk is hollow-top f/b + hollow-bottom-f
      // (back-bottom absent) -> regionsAbsent hollow-bottom: ['b']. The back
      // aperture was EYE-CERTIFIED 2026-07-09 as sitting WITHIN the top zone.
      // (The aperture-zone script's earlier SPANS-BOTH read was a false alarm
      // from degenerate -b body-zone masks: body-top and body-bottom both start
      // at row 112, so the computed seam was unreliable — pixels-decide gave a
      // wrong answer here and the eye overrode it.) laura-duo is a zoned duo,
      // NOT a hollowShape consumer.
      'laura-duo': { archetype: 'duo',  sleeve: null, removeTrim: false, regionsAbsent: { 'hollow-bottom': ['b'] } },   // disk + eye confirmed 2026-07-09
      // lauren: duo, mutual-reveal (laura-duo/erica family). Disk carries ALL FOUR
      // zoned hollows (top f/b + bottom f/b) -> NO regionsAbsent (differs from
      // laura-duo's hollow-bottom ['b']). CERTIFIED WITH DISTINCTION 2026-07-14: all
      // four hollows resolving simultaneously across both views, apertures reading as
      // the opposite panel's interior per the reveal chain; complete disk topology
      // painting as authored. Doctrine FLAGSHIP #3 = maximal simultaneous reveal
      // (charlotte = closed/front-zipper; elise = fall-through-to-base; lauren = full
      // mutual reveal).
      lauren:    { archetype: 'duo', sleeve: null, removeTrim: false },
      lena:      { archetype: 'single', sleeve: null, removeTrim: false },
      lucy:      { archetype: 'trio',   sleeve: null, removeTrim: false, trimDecoupled: { main: ['f', 'b'], noSleeve: ['f', 'b'] }, regionsAbsent: { 'hollow-top': ['f'], 'hollow-bottom': ['b'] } },   // disk-confirmed 2026-07-07 (preflight)
      // marieke: fresh Frankie delivery 2026-07-14 (was AWAITING/zero-masks). FIX:
      // was registered plain single -> loader sought marieke-trim-mask-* (absent) ->
      // trim 404'd. Disk is DECOUPLED (trim-main + trim-no-sleeve both sides, veronique
      // pattern) -> trimDecoupled. No-sleeve trim implies a sleeved garment (sleeves
      // exposed in harness; APO-gated live). Both hollows present.
      marieke:   { archetype: 'single', sleeve: null, removeTrim: false, trimDecoupled: { main: ['f', 'b'], noSleeve: ['f', 'b'] } },   // CERTIFIED 2026-07-14 (long-sleeve PASSED = sleeve-junction SUCCESS counter-specimen)
      // marieke-duo: new style, delivered 2026-07-14. Duo, decoupled trim (both
      // sides), diagonal mutual-reveal hollows (bottom-f + top-b on disk) ->
      // regionsAbsent hollow-top ['f'] + hollow-bottom ['b'] (laura-duo family).
      'marieke-duo': { archetype: 'duo', sleeve: null, removeTrim: false, trimDecoupled: { main: ['f', 'b'], noSleeve: ['f', 'b'] }, regionsAbsent: { 'hollow-top': ['f'], 'hollow-bottom': ['b'] } },   // CERTIFIED 2026-07-14 (diagonal mutual-reveal; crotch fix propagated from single)
      marisa:    { archetype: 'single', sleeve: null, removeTrim: false },   // harness-derived (gateway 2026-07-09, pending confirmation)
      // micah: 2026-07-07 disk inspection resolved the 2026-07-02 record
      // discrepancy; the 2026-07-08 harness cycle confirmed it (all zones
      // cycled both views; the 404 signature matches this regionsAbsent
      // exactly). The "mirror of lucy" claim (micah owns hollow-top-f +
      // hollow-bottom-b) came from CATEGORY REASONING and was wrong; disk
      // carried the true mapping (hollow-top-b + hollow-bottom-f, same as
      // lucy). micah matches lucy per disk + eye, not per a shared-
      // construction axiom. Declaration disk-confirmed.
      micah:     { archetype: 'trio',   sleeve: null, removeTrim: false, regionsAbsent: { 'hollow-top': ['f'], 'hollow-bottom': ['b'] } },
      monique:   { archetype: 'single', sleeve: null, removeTrim: false },   // harness-derived (gateway 2026-07-08, pending confirmation); single, both hollow sides present, no regionsAbsent needed
      nadja:     { archetype: 'single', sleeve: null, removeTrim: false },   // single, no sleeves/zipper, plain trim, both hollows; CERTIFIED 2026-07-14
      natasha:   { archetype: 'single', sleeve: null, removeTrim: false },
      noe:       mkSingle(),   // CERTIFIED 2026-07-14 (was harness-derived gateway 2026-07-09)
      sarah:     mkSingle(),   // CERTIFIED 2026-07-14 (was harness-derived gateway 2026-07-09)
      sofiane:   { archetype: 'single', sleeve: null, removeTrim: false, trimDecoupled: { main: ['f', 'b'], noSleeve: ['f', 'b'] } },
      sonoya:    { archetype: 'single', sleeve: null, removeTrim: false },   // harness-derived (gateway 2026-07-09, pending confirmation)
      tamara:    mkSingle(),   // CERTIFIED 2026-07-14 (was harness-derived gateway 2026-07-09)
      tiffany:   { archetype: 'single', sleeve: null, removeTrim: false },
      veronique: { archetype: 'single', sleeve: null, removeTrim: false, trimDecoupled: { main: ['f', 'b'], noSleeve: ['f', 'b'] } },
      // zoe: trio, no sleeve. Body tokens were already canonical (body-top/
      // body-middle/body-bottom). Her two unzoned-named hollows were content-
      // certified 2026-07-09 (aperture-zone): zoe-hollow-f = single-zone BOTTOM
      // (100%), zoe-hollow-b = single-zone TOP (100%), tokens dropped from the
      // names. Renamed to zoe-hollow-bottom-f + zoe-hollow-top-b -> micah's
      // shape (hollow-top-b + hollow-bottom-f present; front-top + back-bottom
      // absent). regionsAbsent derived from HER disk, not copied from micah.
      zoe:       { archetype: 'trio', sleeve: null, removeTrim: false, regionsAbsent: { 'hollow-top': ['f'], 'hollow-bottom': ['b'] } }   // aperture-content-derived 2026-07-09, pending harness cycle
    },

    // Resting colourway. Initialised from the seed at mount, then mutated by
    // each listener request's merged regions. Every render uses this full
    // state — empty/no-op listener reads can never clear the canvas to blank.
    // Regions the customer never touches keep the seed.
    _currentColourway: null,

    engine: function () {
      var e = this.config.engine || window.DCS;
      if (!e) throw new Error('[bridge] no engine found: set window.DCS or DCSBridge.config.engine');
      return e;
    },

    /* ----- colour key -> swatch URL pair (the reveal's colour step) ----- */

    // "CV-Garnet" -> { f: ".../cv-garnet-f.jpg", b: ".../cv-garnet-b.jpg" }
    // "T-Blue Grey" -> { f: ".../t-blue-grey-f.jpg", b: ".../t-blue-grey-b.jpg" }
    // Returns null if the swatch is not on disk; the caller leaves that region
    // unpainted. This is the fail-soft boundary: DCS displays what the pipeline
    // has delivered, and quietly names what it has not.
    //
    // Slug normalisation (2026-06-18 fix): lowercase, trim, collapse whitespace
    // runs to a single hyphen, then collapse hyphen runs to a single hyphen.
    // The pre-fix slug only lowercased, so "T-Blue Grey" produced "t-blue grey"
    // which URL-encoded to "t-blue%20grey" — a 404 against the hyphenated file
    // on disk (live evidence: daniela picker hit on 2026-06-18). Idempotent on
    // already-correct keys (cv-garnet, n-sorbet, t-vanilla, t-wisteria pass
    // through byte-identical).
    resolveSwatch: async function (key) {
      var lo = String(key).toLowerCase().trim().replace(/\s+/g, '-').replace(/-+/g, '-');
      // Alias step: normalize -> ALIAS -> resolve (interim inline table above).
      lo = this._ALIASES[lo] || lo;
      // Suffix precedence chain (validation bench): per-key swatchTestOverride
      // wins for slugs it names (surgical single-swatch); else falls through
      // to the global swatchTestSuffix (whole-generation flip from
      // `?dcs_gen=tN`); else empty (canonical). All three default empty in
      // committed/production builds — byte-identical to pre-mechanism URLs.
      // Set transiently during real-store validation; runtime config only,
      // never wire a suffixed swatch into a metafield.
      var suffix = (this.config.swatchTestOverride && this.config.swatchTestOverride[lo])
                || this.config.swatchTestSuffix
                || '';
      var slug = lo + suffix;
      var base = this.config.swatchBase;
      var v = '?v=' + this._ASSET_VERSION;
      for (var i = 0; i < this.config.swatchExts.length; i++) {
        var ext = this.config.swatchExts[i];
        try {
          if ((await fetch(base + '/' + slug + '-f.' + ext + v, { method: 'HEAD' })).ok) {
            return { f: base + '/' + slug + '-f.' + ext + v, b: base + '/' + slug + '-b.' + ext + v };
          }
        } catch (e) { /* network error: treat as a miss, try the next extension */ }
      }
      LOG.warn('[bridge] no swatch on disk for', slug, '(fails soft; region left unpainted)');
      return null;
    },

    /* ----- listener request -> engine render args ----- */

    // request = { regions: { <region>: <colourKey>, ... }, trimColor, sleeve, removeTrim }
    // Region keys come from the listener already normalised (top, bottom, ...)
    // and match the engine's render param names for the duo archetype.
    requestToArgs: async function (request) {
      // Treat `request` as immutable: read from it, write only to `args`. The
      // listener may keep the same request object alive across reads (e.g. via
      // window.DCS_LISTENER.last), so any write-back here would poison
      // subsequent renders with shared-mutable-state bugs.
      var args = { styleId: this.config.styleId };
      var regions = (request && request.regions) || {};
      for (var region in regions) {
        if (!Object.prototype.hasOwnProperty.call(regions, region)) continue;
        if (!regions[region]) continue;
        var pair = await this.resolveSwatch(regions[region]);
        if (pair) args[region.toLowerCase()] = pair;   // top, bottom, ...
      }
      if (request && request.trimColor) {
        var trim = await this.resolveSwatch(request.trimColor);
        if (trim) args.trim = trim;
      }
      // Sleeve modifier (work items 2 + 3, 2026-06-15): resolve the listener's
      // sleeve length name into a mask URL pair. Sleeves are universal modifiers
      // — flat under maskBase, never style-prefixed (sleeve-cap-f.png lives at
      // <maskBase>/sleeve-cap-f.png). Lowercase the name (listener emits "3Q";
      // mask filenames are lowercase). "none" or unset → omit args.sleeve.
      //
      // sleeveTrimMask is the sleeve-hem trim mask URL pair, OR null when
      // request.removeTrim is true. The engine paints the hem only when
      // sleeveTrimMask is a URL pair; null means "skip the hem paint."
      //
      // Type-guard (bug fix 2026-06-15): only construct URLs when sleeve is a
      // clean string (the listener's canonical contract — `none`/`cap`/`short`/
      // `half`/`3q`/`long`). Anything non-string (e.g. {unknown: label} from
      // listener's sleeveToken fallback, or a stale URL pair leaking back from
      // a prior render) is skipped → no sleeve. Prevents 404s on stringified
      // garbage like sleeve-[object Object]-f.png.
      if (request && typeof request.sleeve === 'string' && request.sleeve && request.sleeve !== 'none') {
        var name = request.sleeve.toLowerCase();
        args.sleeve = this.buildSleeveMaskUrls(name);
        if (!request.removeTrim) {
          args.sleeveTrimMask = this.buildSleeveTrimMaskUrls(name);
        }
      }
      return args;
    },

    // Sleeve modifier masks live flat under maskBase (no per-style folder,
    // no flatAssets toggle — sleeves are universal). Both views resolve to
    // separate files since back-view sleeves photograph slightly differently
    // from front (single-mask-serves-both was retired 2026-06-11).
    buildSleeveMaskUrls: function (name) {
      var base = this.config.maskBase;
      var v = '?v=' + this._ASSET_VERSION;
      var ms = this.config.maskTestSuffix || '';
      return {
        f: base + '/sleeve-' + name + ms + '-f.png' + v,
        b: base + '/sleeve-' + name + ms + '-b.png' + v
      };
    },

    buildSleeveTrimMaskUrls: function (name) {
      var base = this.config.maskBase;
      var v = '?v=' + this._ASSET_VERSION;
      var ms = this.config.maskTestSuffix || '';
      return {
        f: base + '/sleeve-' + name + '-trim' + ms + '-f.png' + v,
        b: base + '/sleeve-' + name + '-trim' + ms + '-b.png' + v
      };
    },

    /* ----- paint ----- */

    // Translate the request and repaint. Assumes the style is already mounted:
    // call mount() once per page first (production), or rely on the harness's
    // own mount (test).
    render: async function (request) {
      if (!this._mounted) {
        LOG.warn('[bridge] render before mount, ignoring this read');
        return null;
      }
      // Merge customer's changed regions/fields over the resting colourway.
      // Regions the customer never touches keep the seed. Null / empty
      // values are skipped, so an empty or no-op read can never clear the
      // canvas to blank. Every render paints the FULL merged colourway,
      // not just the delta — the canvas is always fully painted, seed
      // where unchosen, customer's choice where chosen.
      if (request && request.regions) {
        for (var region in request.regions) {
          if (!Object.prototype.hasOwnProperty.call(request.regions, region)) continue;
          var v = request.regions[region];
          if (v == null || v === '') continue;
          this._currentColourway.regions[region.toLowerCase()] = v;
        }
      }
      if (request && request.trimColor != null && request.trimColor !== '') {
        this._currentColourway.trimColor = request.trimColor;
      }
      if (request && typeof request.sleeve === 'string' && request.sleeve !== '') {
        this._currentColourway.sleeve = request.sleeve;
      }
      if (request && typeof request.removeTrim === 'boolean') {
        this._currentColourway.removeTrim = request.removeTrim;
      }
      var args = await this.requestToArgs(this._currentColourway);
      await this.engine().render(args);
      LOG.log('[bridge] request', request, 'merged', this._currentColourway, '-> args', args);
      return args;
    },

    /* ----- seed derivation (master cycle + per-style override) ----- */

    // Return the archetype's seed positions in cycle order: body
    // regions bottom → top, then trim last. Recipe long-form zone
    // names are translated to short engine arg names (body-top → top,
    // body-bottom → bottom) and hollows are filtered out (engine-
    // derived, never seeded). Yields per archetype:
    //   single → ['body', 'trim']
    //   duo    → ['bottom', 'top', 'trim']
    //   trio   → ['bottom', 'middle', 'top', 'trim']    (future)
    orderedRegions: function (archetype) {
      var recipe = this._ARCHETYPE_RECIPES[archetype];
      if (!recipe) {
        throw new Error('[bridge] no recipe for archetype "' + archetype + '"');
      }
      var REGION_TO_ARG = this._REGION_TO_ARG;
      var PAINT_ORDER = this._BODY_PAINT_ORDER;
      var argNames = recipe.regions
        .map(function (r) { return REGION_TO_ARG[r]; })
        .filter(Boolean);   // drops hollows (absent from _REGION_TO_ARG)
      var body = argNames.filter(function (n) { return n !== 'trim'; });
      body.sort(function (a, b) { return PAINT_ORDER.indexOf(a) - PAINT_ORDER.indexOf(b); });
      var hasTrim = argNames.indexOf('trim') !== -1;
      return hasTrim ? body.concat(['trim']) : body;
    },

    // Build the per-mount seed colourway from a _SEED_COLOURWAYS entry.
    // Walks the archetype's region order through _SEED_CYCLE (currently
    // a single-element array, so every region resolves to that one
    // neutral), applies the per-style `seed` override (which can
    // replace any derived position by key), then splits the trim slot
    // off into trimColor. Returns { regions, trimColor }; sleeve and
    // removeTrim live on the entry and are read directly by mount()
    // (untouched here).
    //
    // Pass conditions (no override, current single-neutral seed):
    //   single  (daniela) → { regions: { body: 'N-Antique' }, trimColor: 'N-Antique' }
    //   duo     (alicia)  → { regions: { bottom: 'N-Antique', top: 'N-Antique' }, trimColor: 'N-Antique' }
    //   trio    (lucy)    → { regions: { bottom: 'N-Antique', middle: 'N-Antique', top: 'N-Antique' }, trimColor: 'N-Antique' }
    //
    // Override example (minimal nudge, for future velvet/mesh exception):
    //   { archetype: 'duo', seed: { trim: 'N-Cherry' } } — overrides
    //   only the trim slot; body regions stay at N-Antique.
    seedFor: function (entry) {
      // Null-guard (2026-06-19): F1 in mount() throws on a missing entry
      // before seedFor is ever called, so this branch is unreachable on the
      // production path. It exists for defense-in-depth — direct callers
      // (debug console, harness) and any future refactor that moves the
      // call out of mount() get an empty seed rather than a TypeError on
      // entry.archetype. The empty seed paints nothing; F1 remains the
      // primary fail-soft-to-static guard.
      if (!entry || !entry.archetype) {
        return { regions: {}, trimColor: null };
      }
      var order = this.orderedRegions(entry.archetype);
      var cycle = this._SEED_CYCLE;
      var base = {};
      order.forEach(function (r, i) { base[r] = cycle[i % cycle.length]; });
      var override = (entry && entry.seed) || {};
      var seed = Object.assign({}, base, override);
      var trimColor = ('trim' in seed) ? seed.trim : null;
      delete seed.trim;
      return { regions: seed, trimColor: trimColor };
    },

    /* ----- page setup (production stack only) -----
     * The harness registers and mounts the style itself, so these are not used
     * in the harness test. In the store, the page calls mount() once at load.
     */

    // Archetype-driven mount manifest. Looks up the style's archetype from its
    // _SEED_COLOURWAYS entry, then composes URLs through the archetype's recipe
    // (region list + mount-key map + manifest type). Mirrors the harness's
    // buildMountManifest byte-for-byte so the engine sees identical mount keys
    // whether the bridge or the harness produced the manifest. Style-specific
    // patches (today: alicia trim decoupling) apply on top of the recipe.
    buildMountManifest: function () {
      var style = this.config.styleId;
      var seed = this._SEED_COLOURWAYS[style];
      var recipe = this._ARCHETYPE_RECIPES[seed.archetype];
      if (!recipe) {
        throw new Error('[bridge] no recipe for archetype "' + seed.archetype +
          '" declared on style "' + style + '"');
      }
      var base  = this.config.maskBase;
      var flat  = this.config.flatAssets;
      var folder = flat ? '' : ('/' + style);
      var v = '?v=' + this._ASSET_VERSION;
      var ms = this.config.maskTestSuffix || '';
      var REGION_MOUNT_KEY = this._REGION_MOUNT_KEY;
      var maskUrl = function (region, side) {
        var isHollow = region === 'hollow' || region.indexOf('hollow-') === 0;
        var infix = isHollow ? '' : '-mask';   // hollow files omit -mask-
        // Mask suffix lands between the infix and the trailing -<side>.png.
        // Empty (default) → byte-identical to canonical URLs. Non-empty
        // (e.g. '__t1' from ?dcs_gen=t1) → inserts before the side marker
        // for every mask the bridge constructs through this helper.
        return base + folder + '/' + style + '-' + region + infix + ms + '-' + side + '.png' + v;
      };
      var sides = { front: {}, back: {} };
      var frontOnly = recipe.frontOnly || [];
      var backOnly = recipe.backOnly || [];
      recipe.regions.forEach(function (region) {
        var mk = REGION_MOUNT_KEY[region];
        if (!mk) return;   // unknown region — engine doesn't dispatch
        // Per-region sides: a region listed in recipe.frontOnly skips its
        // b-side URL (e.g. charlotte zipper — MANIFEST `sides: [f]`); a region
        // listed in recipe.backOnly skips its f-side URL (e.g. kayoko's split
        // back hollow halves — hollow-top and hollow-middle are back-only by
        // design, the front uses unzoned `hollow` falling through to bottom-
        // color). Without these guards buildMountManifest would emit URLs
        // for both sides unconditionally; loadMaskSet fail-soft would absorb
        // the misses but the 404 probes still fire on the wire, making live-
        // store console-debugging noisier and potentially masking real 404s.
        // A region listed in BOTH would emit no URLs (no-op); current recipes
        // don't exercise that overlap and the loop is defensive against it.
        var regionSides;
        if (frontOnly.indexOf(region) !== -1) regionSides = ['f'];
        else if (backOnly.indexOf(region) !== -1) regionSides = ['b'];
        else regionSides = ['f', 'b'];
        regionSides.forEach(function (sk) {
          sides[sk === 'f' ? 'front' : 'back'][mk] = maskUrl(region, sk);
        });
      });
      // Trim decoupling (generalized 2026-06-25 from the prior style==='alicia'
      // hardcode). Styles with `seed.trimDecoupled` have their default `trim`
      // URL (built from <style>-trim-mask-{f,b}.png by the region loop above)
      // REPLACED by per-variant URLs:
      //   sides.<side>.trim         → <style>-trim-main-mask-<side>.png
      //   sides.<side>.trimNoSleeve → <style>-trim-no-sleeve-mask-<side>.png
      // Both per the declaration's main/noSleeve per-side arrays:
      //   alicia    : { main: ['f', 'b'], noSleeve: ['f', 'b'] }
      //   charlotte : { main: ['b'],      noSleeve: ['f', 'b'] }   ← asymmetric:
      //               front has collar+zipper finish, no main trim binding.
      //
      // Delete semantics: the legacy default `sides.front.trim` and
      // `sides.back.trim` URLs (built from <style>-trim-mask-{f,b}.png, which
      // does NOT exist for decoupled styles — both alicia and charlotte have
      // the legacy single-trim filename absent or being phased out) are
      // DELETED unconditionally before the per-variant emission. Sides absent
      // from `td.main` end with `sides.<side>.trim` removed from the manifest
      // entirely (key absent, not undefined-valued). loadMaskSet's
      // `Object.entries(maskUrls)` iterates only keys present, so the engine
      // sees `masks.<side>.trim === undefined` and silently skips painting
      // via the existing `if (masks.trim)` guard.
      //
      // Fail-soft contract: even when a side IS in `td.main`/`td.noSleeve`
      // but the file happens to be missing on Files (partial upload, asset
      // pipeline mid-flight), loadMaskSet now fail-softs on `trim` and
      // `trimNoSleeve` (engine.js 2026-06-25). The mount completes; the
      // engine renders without the missing variant. Cache-busting via
      // `_ASSET_VERSION` (`?v=3`) ensures a 404 doesn't poison subsequent
      // bumps — re-uploading the asset and bumping the version forces a
      // fresh fetch.
      if (seed.trimDecoupled) {
        var td = seed.trimDecoupled;
        delete sides.front.trim;
        delete sides.back.trim;
        // Refactor 2026-06-26: trim URL construction routes through the
        // `maskUrl` helper above (same path as every other mask URL). Region
        // tokens `trim-main` and `trim-no-sleeve` pass `isHollow=false` →
        // infix `-mask` → produce the canonical filenames byte-identical
        // to the prior inline concat. Routing through `maskUrl` means the
        // generational `maskTestSuffix` applies uniformly here too,
        // without a parallel inline insertion site.
        ['f', 'b'].forEach(function (sk) {
          var side = sk === 'f' ? 'front' : 'back';
          if (td.main && td.main.indexOf(sk) !== -1) {
            sides[side].trim = maskUrl('trim-main', sk);
          }
          if (td.noSleeve && td.noSleeve.indexOf(sk) !== -1) {
            sides[side].trimNoSleeve = maskUrl('trim-no-sleeve', sk);
          }
        });
      }
      return { type: recipe.type, sides: sides };
    },

    // Register + mount the style onto the page's two canvases. Call once.
    // Throws on four failure modes; sidecar's .catch() keeps static gallery up:
    //   - no styleId configured (misconfigured page)
    //   - no seed declared for styleId (Static-Floor leg 3 — F1 fail-fast)
    //   - core mask 404s (engine.mount → loadMaskSet re-throws non-hollow misses)
    //   - seed swatch absent (Static-Floor leg 3' — seed-paint detector below)
    mount: async function (frontCanvas, backCanvas) {
      if (!this.config.styleId) {
        throw new Error('[bridge] mount called with no styleId set; refusing to mount');
      }
      if (!this._SEED_COLOURWAYS[this.config.styleId]) {
        throw new Error('[bridge] no seed declared for styleId "' + this.config.styleId + '"; refusing to mount');
      }
      var DCS = this.engine();
      DCS.registerStyle(this.config.styleId, this.buildMountManifest());
      await DCS.mount({ frontCanvas: frontCanvas, backCanvas: backCanvas, styleId: this.config.styleId });
      this._mounted = true;
      // Initialize resting colourway from the per-style seed and paint it
      // immediately so the canvas shows the product on mount, no listener
      // input required. Bypasses the listener's name→key mapping — feeds
      // canonical swatch keys directly through requestToArgs/resolveSwatch.
      var seed = this._SEED_COLOURWAYS[this.config.styleId];
      var derived = this.seedFor(seed);   // master cycle + per-style override
      this._currentColourway = seed
        ? { regions: Object.assign({}, derived.regions), trimColor: derived.trimColor, sleeve: seed.sleeve, removeTrim: !!seed.removeTrim }
        : { regions: {}, trimColor: null, sleeve: null, removeTrim: false };
      LOG.log('[bridge] mounted', this.config.styleId, 'seed:', this._currentColourway);
      if (seed) {
        var seedArgs = await this.requestToArgs(this._currentColourway);
        // Seed-paint detector: each non-null seed region must have resolved
        // to a swatch URL pair via resolveSwatch, else the seed render would
        // land a blank/partial canvas and the sidecar would still swap to
        // it. Throw here so the sidecar's .catch() keeps the static gallery
        // up. Closes the boundary the previous commit flagged (fail-soft
        // seed). The body MASK absence case is already caught upstream by
        // engine.mount → loadMaskSet's non-hollow throw.
        //
        // Iterates derived.regions (the master-cycle output) — entries
        // no longer carry the colour payload after the 2026-06-19
        // collapse. The sleeve / removeTrim reads above stay byte-
        // identical to the entry's fields.
        for (var region in derived.regions) {
          if (!Object.prototype.hasOwnProperty.call(derived.regions, region)) continue;
          if (!derived.regions[region]) continue;
          if (!seedArgs[region.toLowerCase()]) {
            this._mounted = false;
            throw new Error('[bridge] seed swatch "' + derived.regions[region] + '" did not resolve for region "' + region + '"; rejecting mount');
          }
        }
        await this.engine().render(seedArgs);
        LOG.log('[bridge] seed painted ->', seedArgs);
      }
    }
  };

  window.DCSBridge = Bridge;
})();